import type { DetectedWorkout } from "@/types/workout";

// Common exercise patterns
const EXERCISE_PATTERNS = [
  // Format: "3x10 bench press at 185" or "bench press 3x10 @ 185"
  /(\d+)\s*x\s*(\d+)\s+([\w\s-]+?)(?:\s*(?:at|@)\s*(\d+)\s*(?:lbs?|kg)?)?(?:\s*$|\s*[,.])/gi,
  
  // Format: "bench press: 185 x 3 x 10" or "squat - 225 for 5 sets of 5"
  /([\w\s-]+?)(?:\s*[:\-]\s*)(\d+)\s*(?:lbs?|kg)?\s*(?:x|for)\s*(\d+)\s*(?:sets?)?\s*(?:of|x)?\s*(\d+)?/gi,
  
  // Format: "did bench today" or "hit squats"
  /(?:did|hit|worked|finished|completed)\s+([\w\s-]+?)(?:\s+today|\s+this\s+morning|\s*$)/gi,
];

// Common exercise name mappings
const EXERCISE_ALIASES: Record<string, string> = {
  'bench': 'Bench Press',
  'bench press': 'Bench Press',
  'flat bench': 'Bench Press',
  'incline bench': 'Incline Bench Press',
  'decline bench': 'Decline Bench Press',
  'squat': 'Squat',
  'squats': 'Squat',
  'back squat': 'Squat',
  'front squat': 'Front Squat',
  'deadlift': 'Deadlift',
  'deadlifts': 'Deadlift',
  'conventional deadlift': 'Deadlift',
  'sumo deadlift': 'Sumo Deadlift',
  'rdl': 'Romanian Deadlift',
  'romanian deadlift': 'Romanian Deadlift',
  'ohp': 'Overhead Press',
  'overhead press': 'Overhead Press',
  'shoulder press': 'Overhead Press',
  'military press': 'Overhead Press',
  'rows': 'Barbell Row',
  'barbell row': 'Barbell Row',
  'bent over row': 'Barbell Row',
  'pull ups': 'Pull-Ups',
  'pull-ups': 'Pull-Ups',
  'pullups': 'Pull-Ups',
  'chin ups': 'Chin-Ups',
  'chin-ups': 'Chin-Ups',
  'chinups': 'Chin-Ups',
  'dips': 'Dips',
  'chest': 'Chest Day',
  'back': 'Back Day',
  'legs': 'Leg Day',
  'arms': 'Arm Day',
  'shoulders': 'Shoulder Day',
  'push day': 'Push Day',
  'pull day': 'Pull Day',
  'leg day': 'Leg Day',
  'upper body': 'Upper Body',
  'lower body': 'Lower Body',
  'cardio': 'Cardio',
  'running': 'Running',
  'run': 'Running',
};

function normalizeExerciseName(name: string): string {
  const lower = name.toLowerCase().trim();
  return EXERCISE_ALIASES[lower] || name.split(' ').map(w => 
    w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()
  ).join(' ');
}

// Keywords that indicate workout logging
const WORKOUT_INDICATORS = [
  'worked out', 'workout', 'training', 'trained', 'hit the gym',
  'gym session', 'lifted', 'lifting', 'did chest', 'did back',
  'did legs', 'did arms', 'push day', 'pull day', 'leg day',
  'just finished', 'completed my', 'crushed', 'smashed',
  'today i did', 'today\'s workout', 'morning workout',
  'evening workout', 'after the gym', 'at the gym'
];

/**
 * Check if a message likely contains workout information
 */
export function containsWorkoutInfo(message: string): boolean {
  const lower = message.toLowerCase();
  return WORKOUT_INDICATORS.some(indicator => lower.includes(indicator)) ||
    /\d+\s*x\s*\d+/.test(message) || // Contains sets x reps pattern
    /did\s+\w+\s+today/i.test(message);
}

/**
 * Detect workout details from a message
 */
export function detectWorkoutFromMessage(message: string): DetectedWorkout | null {
  if (!containsWorkoutInfo(message)) {
    return null;
  }

  const exercises: DetectedWorkout['exercises'] = [];
  const seen = new Set<string>();

  // Try each pattern
  for (const pattern of EXERCISE_PATTERNS) {
    pattern.lastIndex = 0;
    let match;
    
    while ((match = pattern.exec(message)) !== null) {
      let name: string;
      let sets: number | undefined;
      let reps: number | undefined;
      let weight: number | undefined;

      // Pattern 1: "3x10 bench press at 185"
      if (!isNaN(parseInt(match[1])) && !isNaN(parseInt(match[2]))) {
        sets = parseInt(match[1]);
        reps = parseInt(match[2]);
        name = normalizeExerciseName(match[3] || 'Unknown');
        weight = match[4] ? parseInt(match[4]) : undefined;
      }
      // Pattern 2: "bench press: 185 x 3 x 10"
      else if (isNaN(parseInt(match[1]))) {
        name = normalizeExerciseName(match[1]);
        weight = match[2] ? parseInt(match[2]) : undefined;
        sets = match[3] ? parseInt(match[3]) : undefined;
        reps = match[4] ? parseInt(match[4]) : undefined;
      }
      // Pattern 3: "did bench today"
      else {
        name = normalizeExerciseName(match[1]);
      }

      const key = name.toLowerCase();
      if (!seen.has(key) && name !== 'Unknown') {
        seen.add(key);
        exercises.push({ name, sets, reps, weight });
      }
    }
  }

  // Also try to detect general workout type
  const lower = message.toLowerCase();
  const workoutTypes = ['chest', 'back', 'legs', 'arms', 'shoulders', 'push day', 'pull day', 'leg day', 'upper body', 'lower body', 'cardio'];
  
  for (const type of workoutTypes) {
    if (lower.includes(type) && !seen.has(type)) {
      exercises.push({ name: normalizeExerciseName(type) });
      seen.add(type);
    }
  }

  if (exercises.length === 0) {
    return null;
  }

  return {
    exercises,
    rawText: message,
  };
}

/**
 * Format detected workout for display
 */
export function formatDetectedWorkout(workout: DetectedWorkout): string {
  return workout.exercises.map(ex => {
    let text = ex.name;
    if (ex.sets && ex.reps) {
      text += `: ${ex.sets}x${ex.reps}`;
    }
    if (ex.weight) {
      text += ` @ ${ex.weight}lbs`;
    }
    return text;
  }).join(', ');
}
